import {
  RepeatabilityMode,
  SBCRewardType,
} from "@/api/privateRequests/sbcs/getSbcSets";
import { CardHeader } from "@/components/organisms/SBCs/CardHeader";
import { RewardBand } from "@/components/organisms/SBCs/RewardBand";
import { SetActionsMenu } from "@/components/organisms/SBCs/SetActionsMenu";
import { SetProgress } from "@/components/organisms/SBCs/SetProgress";
import { VoteChip } from "@/components/organisms/SBCs/VoteChip";
import Card from "@/design-system/Card/Card";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { trackViewPlayerContent } from "@/events/sbcMixpanel";
import { convertSBCPrice } from "@/functions/convertPlayerPrice";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { cn } from "@/lib/utils";
import {
  generateNavigationUrl,
  tierSelector,
} from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { formatExpiryAt, formatTime } from "@repo/shared/utils";
import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

type Props = {
  /** Where the action goes. Only the button navigates; the card does not. */
  to: string;
  disabled?: boolean;
  name: string;
  imageURL: string;
  price: number;
  pointsRequired?: number;
  sbcsCount: number;
  solvedCount: number;
  reward?: SBCRewardType;
  upVotes: number;
  downVotes: number;
  wasUpvoted?: boolean;
  wasDownvoted?: boolean;
  startTime: number;
  endTime: number;
  repeatable: boolean;
  /* The three set actions live in the menu, which `condensed` drops. */
  onMarkSBCSetAsFavorite?: () => void;
  onHideSBCSet?: () => void;
  onCompleteSBCSet?: () => void;
  onUpvote: () => void;
  onDownvote: () => void;
  isFavorite: boolean;
  isHidden: boolean;
  /* Also menu-only: a condensed card has no completion to show. */
  isCompleted?: boolean;
  repeatabilityMode: RepeatabilityMode;
  description: string;
  repeats?: number;
  refreshInterval?: number;
  isNew?: boolean;
  hasRefreshed: boolean;
  /**
   * Front-page dressing: drops the actions menu, which acts on a set of your
   * own, and on phones the progress too — there the card sits beside player
   * cards and the extra rows make it tower over them.
   */
  condensed?: boolean;
  /** Fires on navigation. The front page counts a card open as view_content. */
  onClick?: () => void;
};

const SBCSet = ({
  to,
  disabled,
  repeatable,
  repeats,
  startTime,
  endTime,
  name,
  imageURL,
  price,
  solvedCount,
  sbcsCount,
  pointsRequired,
  reward,
  upVotes,
  downVotes,
  wasUpvoted,
  wasDownvoted,
  onMarkSBCSetAsFavorite,
  onHideSBCSet,
  onCompleteSBCSet,
  onUpvote,
  onDownvote,
  isNew,
  isFavorite,
  isHidden,
  description,
  isCompleted,
  hasRefreshed,
  refreshInterval,
  repeatabilityMode,
  condensed,
  onClick,
}: Props) => {
  const { t } = useTranslation(["sbcs", "common"]);
  const navigate = useNavigate();
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const tier = useSelector(
    /* newDesign: the chip's Tier paints a ring + contrasting foreground, which
       only the new tier table carries. The legacy one colours text and fill the
       same, so the letter disappears into the pill. */
    tierSelector(
      reward?.metaRating?.metarating || 0,
      reward?.metaRating?.playerRoleId,
      true,
    ),
  );

  const timeElapsed = Math.floor(Date.now() / 1000) - startTime;
  const timeUntilRefresh = refreshInterval - (timeElapsed % refreshInterval);
  const expiresIn = formatExpiryAt(endTime);
  // Under a day left reads as urgent.
  const expiresSoon =
    !!endTime && endTime * 1000 - Date.now() < 24 * 60 * 60 * 1000;

  /* One line rather than three icon blocks: the timings qualify the progress
     above them, and read as a sentence at this size. */
  const timings = [
    repeatable
      ? repeats > 0
        ? t("sbcs:set.repeatableWithCount", { count: repeats })
        : t("sbcs:set.repeatable")
      : t("sbcs:set.nonRepeatable"),
    repeatable &&
      repeats > 0 &&
      timeUntilRefresh > 0 &&
      t("sbcs:setCard.refreshesIn", {
        time: formatTime(timeUntilRefresh * 1000),
      }),
  ].filter(Boolean);
  // Far-out end dates are placeholders for "never", so they are hidden.
  const expiry = !endTime
    ? t("sbcs:set.noExpiry")
    : expiresIn
      ? t("sbcs:setCard.expiresIn", { time: expiresIn })
      : undefined;

  const timingsLine = (
    <span className="truncate text-[11px] text-foreground-faint">
      <span>{timings.join(" · ")}</span>
      {expiry && (
        <>
          <span>{" · "}</span>
          <span className={cn(expiresSoon && "text-negative-foreground")}>
            {expiry}
          </span>
        </>
      )}
    </span>
  );

  return (
    <NavLink to={to} className="h-full" onClick={onClick}>
      <Card
        hoverable={!disabled}
        className={cn(
          "relative flex h-full cursor-pointer flex-col overflow-hidden desktop:p-2",
          disabled && "pointer-events-none cursor-default",
        )}
      >
        {disabled && (
          <div className="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px] brightness-75">
            <span className="rounded border bg-background px-6 py-4 text-ea-red">
              {t("sbcs:set.issues")}
            </span>
          </div>
        )}

        <CardHeader
          imageURL={imageURL}
          name={name}
          price={convertSBCPrice(price)}
          pointsRequired={pointsRequired}
          badge={
            (isNew || hasRefreshed) && (
              <NewTag
                tag={isNew ? t("common:tags.new") : t("common:tags.refreshed")}
              />
            )
          }
          trailing={
            condensed ? undefined : (
              <SetActionsMenu
                isFavorite={isFavorite}
                isHidden={isHidden}
                isCompleted={isCompleted}
                disableComplete={repeatabilityMode === "UNLIMITED"}
                onMarkAsFavorite={onMarkSBCSetAsFavorite}
                onHide={onHideSBCSet}
                onComplete={onCompleteSBCSet}
              />
            )
          }
        />

        <SetProgress
          className={cn("px-5 pb-4", condensed && "hidden desktop:flex")}
          solvedCount={solvedCount}
          sbcsCount={sbcsCount}
          meta={timingsLine}
        />
        {/* The timings are the progress row's own meta, so a card that drops
            the row on phones still prints them. */}
        {condensed && <p className="px-5 pb-4 desktop:hidden">{timingsLine}</p>}

        <div className="flex-1" />

        <RewardBand
          reward={reward}
          rewardUrl={extractRewardUrl(reward)}
          description={description}
          tier={tier}
          onViewPlayer={
            // Desktop only: on touch the whole card opens the set.
            !isTouch && reward?.type === "player"
              ? () => {
                  trackViewPlayerContent();
                  navigate(
                    generateNavigationUrl({
                      resourceId: reward.resourceId,
                      name,
                      basePath: "/players",
                    }),
                  );
                }
              : undefined
          }
          trailing={
            <VoteChip
              upVotes={upVotes}
              downVotes={downVotes}
              wasUpvoted={wasUpvoted}
              wasDownvoted={wasDownvoted}
              onUpvote={onUpvote}
              onDownvote={onDownvote}
            />
          }
        />
      </Card>
    </NavLink>
  );
};

export default SBCSet;
